import MarketingImage from '@/components/landing/MarketingImage';
import { detachPageSeo } from '@/utils/pageSeo';
import React, { useEffect, useMemo } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import NotFound from '@/pages/NotFound';
import { BlogPost, blogPostsBySlug } from '@/data/blogPosts';
import { BLOG_AUTHOR_NAME, SITE_URL, getAbsoluteSiteUrl } from '@/data/blogSeoMetadata';
import { OG_IMAGE_HEIGHT, OG_IMAGE_TYPE, OG_IMAGE_WIDTH, PUBLISHER_LOGO_URL } from '@/data/socialSeo';
import styles from './BlogPostPage.module.css';

const FOOTER_DISCLOSURE =
  'CalorieScience is a workflow and education platform that supports practitioner judgement. It is UK and EU GDPR compliant; HIPAA is in progress. It is not a diagnostic tool, not a medical device, and does not replace clinical assessment by a qualified practitioner.';

const stripHtml = (value: string) =>
  value
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#x27;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

const getReadingTimeMinutes = (contentHtml: string) => {
  const words = stripHtml(contentHtml).split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
};

const createMeta = (attributes: Record<string, string>) => {
  const element = document.createElement('meta');
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
  element.setAttribute('data-blog-seo', 'true');
  document.head.appendChild(element);
};

const createLink = (attributes: Record<string, string>) => {
  const element = document.createElement('link');
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
  element.setAttribute('data-blog-seo', 'true');
  document.head.appendChild(element);
};

const createJsonLd = (value: Record<string, unknown>) => {
  const element = document.createElement('script');
  element.type = 'application/ld+json';
  element.textContent = JSON.stringify(value);
  element.setAttribute('data-blog-seo', 'true');
  document.head.appendChild(element);
};

const useBlogSeo = (post: BlogPost | undefined) => {
  useEffect(() => {
    if (!post) return;

    const previousTitle = document.title;
    const restorePageSeo = detachPageSeo();
    document.querySelectorAll('[data-blog-seo="true"]').forEach(element => element.remove());

    const canonicalUrl = getAbsoluteSiteUrl(post.href);
    const imageUrl = getAbsoluteSiteUrl(post.ogImagePath);
    const imageAlt = post.imageAlt ?? post.title;

    document.title = post.metaTitle;
    createMeta({ name: 'description', content: post.metaDescription });
    createMeta({ property: 'og:title', content: post.metaTitle });
    createMeta({ property: 'og:description', content: post.metaDescription });
    createMeta({ property: 'og:url', content: canonicalUrl });
    createMeta({ property: 'og:type', content: 'article' });
    createMeta({ property: 'og:image', content: imageUrl });
    createMeta({ property: 'og:image:type', content: OG_IMAGE_TYPE });
    createMeta({ property: 'og:image:width', content: String(OG_IMAGE_WIDTH) });
    createMeta({ property: 'og:image:height', content: String(OG_IMAGE_HEIGHT) });
    createMeta({ property: 'og:image:alt', content: imageAlt });
    createMeta({ name: 'twitter:card', content: 'summary_large_image' });
    createMeta({ name: 'twitter:title', content: post.metaTitle });
    createMeta({ name: 'twitter:description', content: post.metaDescription });
    createMeta({ name: 'twitter:image', content: imageUrl });
    createMeta({ name: 'twitter:image:alt', content: imageAlt });
    createLink({ rel: 'canonical', href: canonicalUrl });
    createLink({ rel: 'alternate', hreflang: 'en-GB', href: canonicalUrl });

    createJsonLd({
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.metaTitle,
      description: post.metaDescription,
      author: {
        '@type': post.authorName ? 'Organization' : 'Person',
        name: post.authorName ?? BLOG_AUTHOR_NAME,
        url: SITE_URL,
      },
      publisher: {
        '@type': 'Organization',
        name: 'CalorieScience',
        logo: {
          '@type': 'ImageObject',
          url: PUBLISHER_LOGO_URL,
        },
      },
      datePublished: post.publishedDate,
      dateModified: post.publishedDate,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonicalUrl,
      },
      image: imageUrl,
    });

    createJsonLd({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: SITE_URL,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Insights',
          item: `${SITE_URL}/#blogs`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: post.title,
          item: canonicalUrl,
        },
      ],
    });

    if (post.faqs.length > 0) {
      createJsonLd({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: post.faqs.map(faq => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      });
    }

    return () => {
      document.querySelectorAll('[data-blog-seo="true"]').forEach(element => element.remove());
      restorePageSeo();
      document.title = previousTitle;
    };
  }, [post]);
};

const BlogPostPage: React.FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const post = blogPostsBySlug[slug];
  const readingTime = useMemo(
    () => (post ? getReadingTimeMinutes(post.contentHtml) : 0),
    [post]
  );

  useBlogSeo(post);

  if (!post) {
    return <NotFound />;
  }

  return (
    <div className={styles.page}>
      <Navbar />
      <main className={styles.main}>
        <article className={styles.article}>
          <Link to="/#blogs" className={styles.backLink}>
            <ArrowLeft aria-hidden="true" size={16} strokeWidth={2.4} />
            Back
          </Link>

          <header className={styles.hero}>
            <p className={styles.category}>{post.category}</p>
            <h1 className={styles.title}>{post.title}</h1>
            <p className={styles.description}>{post.metaDescription}</p>
            <div className={styles.metaRow}>
              <span>
                By <Link to="/#mission">{post.authorName ?? BLOG_AUTHOR_NAME}</Link>
              </span>
              <span>{post.displayDate}</span>
              <span>{readingTime} min read</span>
            </div>
            <MarketingImage loading="eager" src={post.image} alt={post.imageAlt ?? post.title} className={styles.heroImage} />
          </header>

          <div
            className={styles.content}
            dangerouslySetInnerHTML={{ __html: post.contentHtml }}
          />

          <p className={styles.disclaimer}>{FOOTER_DISCLOSURE}</p>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPostPage;
